import React from 'react';
import { createPortal } from 'react-dom';
import WebDashboardLayoutModeField from '../../Components/WebDashboardLayoutModeField';
import {
    createDefaultWebDashboardTab,
    WEB_DASHBOARD_LAYOUT_MODES,
} from '../../utils/webDashboardLayout';

const resetAddDashboardState = {
    adding: false,
    dashboardName: 'Dashboard',
    dashboardRoles: ['super', 'owner'],
    dashboardLayoutMode: WEB_DASHBOARD_LAYOUT_MODES.PIXELS,
};

const TemplateAddDashboardPopup = ({
    t,
    addDashboard,
    addPopupAnchor,
    addPopupRef,
    allRoles,
    webDashboardT,
    details,
    setAddDashboard,
    setAddPopupAnchor,
    handleRoleSelection,
    setDetails,
    setCurrentDashboard,
    setSearchParams,
}) => {
    if (typeof document === 'undefined' || !addDashboard.adding || !addPopupAnchor) {
        return null;
    }

    return createPortal(
        <div
            ref={addPopupRef}
            className="role-editor-popup role-editor-popup--add role-editor-popup--portal"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
                position: 'fixed',
                top: addPopupAnchor.top,
                left: addPopupAnchor.left,
                transform: 'translateX(-50%)',
                zIndex: 100000,
                backgroundColor: 'var(--cell-background)',
                border: '1px solid var(--modal-border)',
                borderRadius: '8px',
                padding: '10px',
                boxShadow: '0 12px 30px rgba(0,0,0,0.35)',
                width: '340px',
                maxWidth: 'calc(100vw - 24px)',
                maxHeight: 'calc(100vh - 24px)',
                overflowY: 'auto',
                overflowX: 'hidden',
            }}
        >
            <h4
                style={{
                    marginBottom: '10px',
                    fontSize:
                        'calc(var(--ui-font-size) * var(--font-size-multiplier))',
                }}
            >
                {t.addTabTitle || t.dashboardAddTab || 'Add Tab'}
            </h4>

            <div style={{ marginBottom: 10 }}>
                <div
                    style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#AEEAFF',
                        marginBottom: 8,
                    }}
                >
                    {t.tabName || 'Tab name'}
                </div>
                <input
                    type="text"
                    className="add-column"
                    maxLength={50}
                    value={addDashboard.dashboardName || ''}
                    onChange={(e) =>
                        setAddDashboard((prev) => ({
                            ...prev,
                            dashboardName: e.target.value,
                        }))
                    }
                    style={{
                        paddingInlineStart: '10px',
                        color: 'var(--cell-color)',
                        caretColor: 'var(--cell-color)',
                        width: '100%',
                    }}
                />
            </div>

            <div style={{ marginBottom: 10 }}>
                <WebDashboardLayoutModeField
                    value={addDashboard.dashboardLayoutMode}
                    onChange={(nextMode) =>
                        setAddDashboard((prev) => ({
                            ...prev,
                            dashboardLayoutMode: nextMode,
                        }))
                    }
                    title={webDashboardT.layoutModeLabel || 'Layout Mode'}
                    helperText={
                        webDashboardT.layoutModeHelper ||
                        'Choose how widget sizes and positions are stored for this tab.'
                    }
                    translations={webDashboardT}
                    compact
                />
            </div>

            <div
                className="multi-select-container"
                style={{ maxHeight: '200px', overflowY: 'auto' }}
            >
                {allRoles
                    .filter((role) => !['super', 'owner'].includes(role))
                    .map((role) => (
                        <div
                            key={role}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRoleSelection(role);
                            }}
                            style={{
                                padding: '4px 0',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                fontSize:
                                    'calc(var(--body-font-size) * var(--font-size-multiplier))',
                            }}
                        >
                            <input
                                type="checkbox"
                                readOnly
                                checked={addDashboard.dashboardRoles.includes(role)}
                                style={{
                                    marginRight: '8px',
                                    cursor: 'pointer',
                                }}
                            />
                            {role}
                        </div>
                    ))}
            </div>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    marginTop: '10px',
                }}
            >
                <button
                    type="button"
                    className="btn btn-cancel"
                    style={{
                        fontSize:
                            'calc(var(--body-font-size) * var(--font-size-multiplier))',
                        padding: '6px 12px',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setAddDashboard(resetAddDashboardState);
                        setAddPopupAnchor(null);
                    }}
                >
                    {t.cancel}
                </button>

                <button
                    type="button"
                    className="btn edit-btn-save"
                    style={{
                        fontSize:
                            'calc(var(--body-font-size) * var(--font-size-multiplier))',
                        padding: '6px 12px',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();

                        const trimmedName = String(
                            addDashboard.dashboardName || ''
                        ).trim();

                        const newDashboardEntry = createDefaultWebDashboardTab({
                            id: `new-${Date.now()}`,
                            name: trimmedName.length ? trimmedName : 'Dashboard',
                            accessRole: Array.isArray(addDashboard.dashboardRoles)
                                ? addDashboard.dashboardRoles
                                : ['super', 'owner'],
                            layoutMode: addDashboard.dashboardLayoutMode,
                        });

                        setDetails((prevDetails) => ({
                            ...prevDetails,
                            webdashboard: [
                                ...(prevDetails.webdashboard || []),
                                newDashboardEntry,
                            ],
                        }));

                        const newIndex =
                            (details.webdashboard?.length || 0);
                        setCurrentDashboard(newIndex);
                        setSearchParams(
                            (prev) => {
                                prev.set(
                                    'dashboardIdx',
                                    newIndex.toString()
                                );
                                return prev;
                            },
                            { replace: true }
                        );

                        setAddDashboard(resetAddDashboardState);
                        setAddPopupAnchor(null);
                    }}
                >
                    {t.add}
                </button>
            </div>
        </div>,
        document.body
    );
};

export default TemplateAddDashboardPopup;
